import type { TokenCredential } from "@azure/identity";
import { INTAKE_AGENT_NAME } from "@m8t-stack/api-contract";
import { deployPromptAdvisor } from "./deploy-prompt-advisor.js";

export const INTAKE_PERSONA = "ezra-intake";
/** Re-exported under the historical name so callers are unaffected. The single
 *  declared name for this agent lives in @m8t-stack/api-contract — see that
 *  module's doc comment for why it must not fork again. */
export const INTAKE_AGENT = INTAKE_AGENT_NAME;

/**
 * Every failure `deployPromptAdvisor` can raise, mapped to this seam's own code.
 * Adding an ADVISOR_* code without adding a row here lets it leak to callers
 * unrenamed — which is exactly how ADVISOR_BAD_TOOLS went unmapped.
 */
// A null-prototype map, not an object literal: `table[err.code]` on a plain
// object resolves inherited members, so a code of "toString" or "constructor"
// would return a truthy FUNCTION and be assigned as the error code. Unreachable
// with today's ADVISOR_* set, but the if-chain this replaced could not do it,
// and a lookup table should not quietly widen what counts as a match.
const ADVISOR_TO_INTAKE_CODE: ReadonlyMap<string, string> = new Map([
  ["ADVISOR_PERSONA_MISSING", "INTAKE_PERSONA_MISSING"],
  ["ADVISOR_PERSONA_EMPTY", "INTAKE_PERSONA_EMPTY"],
  ["ADVISOR_NO_MODEL", "INTAKE_NO_MODEL"],
  ["ADVISOR_BAD_EFFORT", "INTAKE_BAD_EFFORT"],
  ["ADVISOR_BAD_TOOLS", "INTAKE_BAD_TOOLS"],
]);

/**
 * Deploy the no-brain onboarding intake agent (`ezra-intake`) from the intake persona.
 * Reads model + reasoning from the persona frontmatter; the body (placeholder-
 * free) becomes the instructions. ~10s via createPromptVersion. Idempotent in
 * the Foundry sense (each call = a new version).
 *
 * The intake persona declares three fillable-fields — `founder_identity_note`,
 * `chosen_model_note`, and `subscription_inventory_note`; `deployIntakeAgent`
 * forwards optional `fieldOverrides` (e.g. the founder's Azure identity and
 * chosen-model note, injected by `m8t bootstrap ui`) which are merged over
 * the persona defaults at deploy time.
 */
export async function deployIntakeAgent(args: {
  credential: TokenCredential;
  endpoint: string;
  repoRoot: string;
  /** Model chosen by the cascade. Omitted → the persona frontmatter floor. */
  model?: string;
  fieldOverrides?: Record<string, string>;
}): Promise<string> {
  try {
    return await deployPromptAdvisor({
      credential: args.credential,
      endpoint: args.endpoint,
      repoRoot: args.repoRoot,
      persona: INTAKE_PERSONA,
      agentName: INTAKE_AGENT,
      model: args.model,
      fieldOverrides: args.fieldOverrides,
    });
  } catch (e: unknown) {
    // Re-map ADVISOR_* codes to this module's own INTAKE_* codes, so callers
    // observe error codes named for the intake seam rather than the shared
    // deploy-prompt-advisor helper underneath it.
    //
    // A TABLE, not an if-chain. The chain form required every new ADVISOR_* code
    // to be remembered here, and one was not: ADVISOR_BAD_TOOLS leaked through
    // unmapped from the day tools-forwarding shipped, so a malformed intake tools
    // block surfaced a code from a module the caller does not know exists.
    if (e && typeof e === "object" && "code" in e) {
      const err = e as { code: string; message: string; hint?: string; cause?: unknown };
      const intakeCode = ADVISOR_TO_INTAKE_CODE.get(err.code);
      if (intakeCode) {
        const { LocalCliError } = await import("./errors.js");
        throw new LocalCliError({
          code: intakeCode,
          message: err.message,
          hint: err.hint,
          cause: err.cause,
        });
      }
    }
    throw e;
  }
}
